/**
 * @deprecated This service is deprecated and will be removed in a future version.
 * 
 * Use `systemTemplateService` from './systemTemplateService' instead.
 * 
 * The admin_email_templates collection is no longer used.
 * System templates are now stored in the email_templates collection with createdBy: 'admin' or 'system'.
 */

import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  Timestamp,
  increment,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { EmailTemplate, EmailTemplateStyles } from '../types/emailBuilder';
import { EmailBlockData } from '../components/emailBuilder/ModernEmailBuilder';

export interface AdminEmailTemplate {
  id?: string;
  name: string;
  description: string;
  subject: string;
  category: 'newsletter' | 'promotional' | 'transactional' | 'welcome' | 'announcement' | 'other';
  tags: string[];
  
  // Template Content (from ModernEmailBuilder)
  designJson: {
    blocks: EmailBlockData[];
    styles: EmailTemplateStyles;
  };
  htmlContent: string; // Generated HTML for preview/export
  
  // Metadata
  previewImage?: string; // Thumbnail/preview image URL
  thumbnail?: string; // Small preview image
  
  // Publishing
  isPublished: boolean; // Whether template is visible to users
  isFeatured: boolean; // Featured templates shown first
  publishedAt?: Timestamp;
  
  // Analytics
  usageCount: number; // How many times template was used
  lastUsedAt?: Timestamp;
  
  // Admin Info
  createdBy: string; // Admin user ID
  createdAt: Timestamp;
  updatedAt: Timestamp;
  updatedBy?: string;
  
  // Versioning
  version: number;
  previousVersions?: string[]; // Array of template IDs for version history
}

export interface CreateTemplateData {
  name: string;
  description: string;
  subject: string;
  category: AdminEmailTemplate['category'];
  tags: string[];
  designJson: {
    blocks: EmailBlockData[];
    styles: EmailTemplateStyles;
  };
  htmlContent: string;
  previewImage?: string;
  isPublished?: boolean;
  isFeatured?: boolean;
}

export interface TemplateStats {
  usageCount: number;
  lastUsedAt?: Date;
  createdAt: Date;
  publishedAt?: Date;
}

class AdminEmailTemplateService {
  private collectionName = 'admin_email_templates';

  /**
   * Create a new admin email template
   */
  async createTemplate(templateData: CreateTemplateData, createdBy: string): Promise<AdminEmailTemplate> {
    try {
      const now = Timestamp.now();
      const templateDoc = {
        name: templateData.name,
        description: templateData.description,
        subject: templateData.subject,
        category: templateData.category,
        tags: templateData.tags || [],
        designJson: templateData.designJson,
        htmlContent: templateData.htmlContent,
        previewImage: templateData.previewImage || '',
        thumbnail: templateData.previewImage || '',
        isPublished: templateData.isPublished || false,
        isFeatured: templateData.isFeatured || false,
        publishedAt: templateData.isPublished ? now : undefined,
        usageCount: 0,
        lastUsedAt: undefined,
        createdBy,
        createdAt: now,
        updatedAt: now,
        updatedBy: createdBy,
        version: 1,
        previousVersions: [],
      };

      const docRef = await addDoc(collection(db, this.collectionName), templateDoc);
      
      return {
        id: docRef.id,
        ...templateDoc,
      } as AdminEmailTemplate;
    } catch (error) {
      console.error('Error creating admin email template:', error);
      throw new Error('Failed to create template');
    }
  }

  /**
   * Update an existing template
   */
  async updateTemplate(
    templateId: string, 
    updates: Partial<AdminEmailTemplate>,
    updatedBy: string
  ): Promise<void> {
    try {
      const templateRef = doc(db, this.collectionName, templateId);
      const updateData: any = {
        ...updates,
        updatedAt: Timestamp.now(),
        updatedBy,
      };

      // If publishing for the first time, set publishedAt
      if (updates.isPublished === true) {
        const currentTemplate = await this.getTemplate(templateId);
        if (!currentTemplate.publishedAt) {
          updateData.publishedAt = Timestamp.now();
        }
      }

      // Increment version if designJson or htmlContent changed
      if (updates.designJson || updates.htmlContent) {
        updateData.version = increment(1);
      }

      await updateDoc(templateRef, updateData);
    } catch (error) {
      console.error('Error updating admin email template:', error);
      throw new Error('Failed to update template');
    }
  }

  /**
   * Delete a template
   */
  async deleteTemplate(templateId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.collectionName, templateId));
    } catch (error) {
      console.error('Error deleting admin email template:', error);
      throw new Error('Failed to delete template');
    }
  }

  /**
   * Get a single template by ID
   */
  async getTemplate(templateId: string): Promise<AdminEmailTemplate> {
    try {
      const docRef = doc(db, this.collectionName, templateId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Template not found');
      }

      return {
        id: docSnap.id,
        ...docSnap.data(),
      } as AdminEmailTemplate;
    } catch (error) {
      console.error('Error getting admin email template:', error);
      throw new Error('Failed to get template');
    }
  }

  /**
   * Get all templates (admin view - includes unpublished)
   */
  async getAllTemplates(): Promise<AdminEmailTemplate[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as AdminEmailTemplate[];
    } catch (error) {
      console.error('Error getting all admin email templates:', error);
      throw new Error('Failed to get templates');
    }
  }

  /**
   * Get only published templates (user view)
   */
  async getPublishedTemplates(): Promise<AdminEmailTemplate[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('isPublished', '==', true),
        orderBy('isFeatured', 'desc'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as AdminEmailTemplate[];
    } catch (error) {
      console.error('Error getting published templates:', error);
      throw new Error('Failed to get published templates');
    }
  }

  /**
   * Get templates by category
   */
  async getTemplatesByCategory(category: string, publishedOnly: boolean = false): Promise<AdminEmailTemplate[]> {
    try {
      if (publishedOnly) {
        const q = query(
          collection(db, this.collectionName),
          where('category', '==', category),
          where('isPublished', '==', true),
          orderBy('isFeatured', 'desc'),
          orderBy('createdAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as AdminEmailTemplate[];
      } else {
        const q = query(
          collection(db, this.collectionName),
          where('category', '==', category),
          orderBy('isFeatured', 'desc'),
          orderBy('createdAt', 'desc')
        );
        const querySnapshot = await getDocs(q);
        
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        })) as AdminEmailTemplate[];
      }
    } catch (error) {
      console.error('Error getting templates by category:', error);
      throw new Error('Failed to get templates by category');
    }
  }

  /**
   * Get featured templates
   */
  async getFeaturedTemplates(): Promise<AdminEmailTemplate[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('isPublished', '==', true),
        where('isFeatured', '==', true),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as AdminEmailTemplate[];
    } catch (error) {
      console.error('Error getting featured templates:', error);
      throw new Error('Failed to get featured templates');
    }
  }

  /**
   * Publish a template
   */
  async publishTemplate(templateId: string, updatedBy: string): Promise<void> {
    try {
      const templateRef = doc(db, this.collectionName, templateId);
      const currentTemplate = await this.getTemplate(templateId);
      
      await updateDoc(templateRef, {
        isPublished: true,
        publishedAt: currentTemplate.publishedAt || Timestamp.now(),
        updatedAt: Timestamp.now(),
        updatedBy,
      });
    } catch (error) {
      console.error('Error publishing template:', error);
      throw new Error('Failed to publish template');
    }
  }

  /**
   * Unpublish a template
   */
  async unpublishTemplate(templateId: string, updatedBy: string): Promise<void> {
    try {
      const templateRef = doc(db, this.collectionName, templateId);
      await updateDoc(templateRef, {
        isPublished: false,
        updatedAt: Timestamp.now(),
        updatedBy,
      });
    } catch (error) {
      console.error('Error unpublishing template:', error);
      throw new Error('Failed to unpublish template');
    }
  }

  /**
   * Set template as featured/unfeatured
   */
  async setFeatured(templateId: string, isFeatured: boolean, updatedBy: string): Promise<void> {
    try {
      const templateRef = doc(db, this.collectionName, templateId);
      await updateDoc(templateRef, {
        isFeatured,
        updatedAt: Timestamp.now(),
        updatedBy,
      });
    } catch (error) {
      console.error('Error setting featured status:', error);
      throw new Error('Failed to set featured status');
    }
  }

  /**
   * Increment usage count when template is used
   */
  async incrementUsageCount(templateId: string): Promise<void> {
    try {
      const templateRef = doc(db, this.collectionName, templateId);
      await updateDoc(templateRef, {
        usageCount: increment(1),
        lastUsedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error incrementing usage count:', error);
      throw new Error('Failed to increment usage count');
    }
  }

  /**
   * Get template statistics
   */
  async getTemplateStats(templateId: string): Promise<TemplateStats> {
    try {
      const template = await this.getTemplate(templateId);
      return {
        usageCount: template.usageCount,
        lastUsedAt: template.lastUsedAt?.toDate(),
        createdAt: template.createdAt.toDate(),
        publishedAt: template.publishedAt?.toDate(),
      };
    } catch (error) {
      console.error('Error getting template stats:', error);
      throw new Error('Failed to get template stats');
    }
  }

  /**
   * Search templates by name or description
   */
  async searchTemplates(searchTerm: string, publishedOnly: boolean = false): Promise<AdminEmailTemplate[]> {
    try {
      // Firestore doesn't support full-text search, so we'll get all and filter
      const templates = publishedOnly 
        ? await this.getPublishedTemplates()
        : await this.getAllTemplates();
      
      const lowerSearchTerm = searchTerm.toLowerCase();
      return templates.filter(template => 
        template.name.toLowerCase().includes(lowerSearchTerm) ||
        template.description.toLowerCase().includes(lowerSearchTerm) ||
        template.tags.some(tag => tag.toLowerCase().includes(lowerSearchTerm))
      );
    } catch (error) {
      console.error('Error searching templates:', error);
      throw new Error('Failed to search templates');
    }
  }
}

export const adminEmailTemplateService = new AdminEmailTemplateService();


